// Step 24 — pure helpers for the AI Deal Execution & Closing
// Readiness workspace.
//
// All logic here is deterministic and side-effect free. It composes
// data three EXISTING endpoints already emit:
//   - getDealSituationLive()  (Step 12)  → buyer portfolio,
//     preparedness observations, cross-buyer patterns, suggested
//     actions, deal snapshot (with known_risks).
//   - listDealActions()       (Step 13)  → DealAction rows with
//     status, priority, due_at, blocker_count, acquirer_id, etc.
//   - listContracts()         (Step 16)  → ContractDraft rows with
//     status, uncertainties_count, variables_count, acquirer_id.
//
// Guardrails:
//   - NO invented readiness percentage, "closing probability", "deal
//     health score", "confidence", or synthetic overall traffic-light.
//   - Every derived row preserves the underlying source_type,
//     source_id, and as_of timestamp. Missing provenance stays null.
//   - fact_or_inference labels ride through VERBATIM.
//   - Cross-domain connections are established ONLY via explicit
//     IDs the backend already returns (acquirer_id, deal_action_id,
//     outreach_draft_id, source.buyer_intel_item_id, etc.). We
//     never guess relationships from text.
//   - No fabricated timestamps. No fabricated statuses. No fabricated
//     "obligations" — the backend has no obligation model
//     (AIObligations.jsx documents this); we surface preparedness
//     observations from the deal-situation aggregate instead and
//     label them exactly for what they are.

import {
  extractPreparednessFindings,
  extractPendingActionFindings,
  extractRiskFindings,
  KIND,
  SEVERITY,
} from "../intelligence/helpers";

// ── Neutral wording used across the workspace ──────────────────────

export const READINESS_LANGUAGE = {
  FOLLOW_UP: "Kräver uppföljning",
  OPEN: "Öppet enligt systemstatus",
  BLOCKED: "Blockerad enligt systemstatus",
  OVERDUE: "Över förfallodatum",
  PENDING_REVIEW: "Kräver mänsklig granskning",
  UNCERTAINTY: "Osäkerhet registrerad",
  NO_STATUS: "Ingen auktoritativ status registrerad",
};


// ── Action lifecycle mirrors ───────────────────────────────────────
//
// Mirrors backend/ai_native/deal_actions/lifecycle.py sets. Kept as
// module constants (not re-derived per call) so the UI never drifts
// out of sync with the backend definition of "open" / "terminal".

export const ACTION_OPEN_STATUSES = new Set([
  "proposed", "pending_review", "approved", "in_progress", "blocked",
]);
export const ACTION_TERMINAL_STATUSES = new Set([
  "completed", "rejected", "cancelled", "superseded",
]);


// ── Contract lifecycle mirrors ─────────────────────────────────────

export const CONTRACT_EDITABLE_STATUSES = new Set([
  "drafting", "pending_review",
]);
export const CONTRACT_TERMINAL_STATUSES = new Set([
  "signed", "rejected", "cancelled", "superseded",
]);


// ── Deterministic overdue check ────────────────────────────────────

function _isOverdue(iso, now) {
  if (!iso) return false;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return false;
  return t < now;
}

function _iso(v) {
  if (!v) return null;
  const t = Date.parse(v);
  if (!Number.isFinite(t)) return null;
  return new Date(t).toISOString();
}


// ── Action projections ─────────────────────────────────────────────

export function executionActionRows(actions, { now = Date.now() } = {}) {
  if (!Array.isArray(actions)) return [];
  return actions.map((a) => ({
    id: a.id,
    title: a.title,
    action_type: a.action_type,
    status: a.status,
    priority: a.priority || null,
    readiness: a.readiness || null,
    acquirer_id: a.acquirer_id ?? null,
    acquirer_name: a.acquirer_name || null,
    owner_id: a.owner_id ?? null,
    due_at: a.due_at || null,
    is_overdue: (
      ACTION_OPEN_STATUSES.has(a.status) && _isOverdue(a.due_at, now)
    ),
    is_open: ACTION_OPEN_STATUSES.has(a.status),
    is_terminal: ACTION_TERMINAL_STATUSES.has(a.status),
    blocker_count: Number(a.blocker_count ?? 0),
    prerequisite_unsatisfied_count: Number(
      a.prerequisite_unsatisfied_count ?? 0,
    ),
    updated_at: a.updated_at || null,
  }));
}

export function executionActionCounts(actions, opts) {
  const rows = executionActionRows(actions, opts);
  return {
    open: rows.filter((r) => r.is_open).length,
    blocked: rows.filter((r) => r.status === "blocked").length,
    overdue: rows.filter((r) => r.is_overdue).length,
    in_progress: rows.filter((r) => r.status === "in_progress").length,
    completed: rows.filter((r) => r.status === "completed").length,
    proposed: rows.filter((r) => r.status === "proposed").length,
    pending_review: rows.filter((r) => r.status === "pending_review").length,
    approved: rows.filter((r) => r.status === "approved").length,
    total: rows.length,
  };
}


// ── Contract projections ───────────────────────────────────────────

export function executionContractRows(contracts) {
  if (!Array.isArray(contracts)) return [];
  return contracts.map((c) => ({
    id: c.id,
    title: c.title || `#${c.id}`,
    kind: c.kind,
    status: c.status,
    acquirer_id: c.acquirer_id ?? null,
    acquirer_name: c.acquirer_name || null,
    deal_action_id: c.deal_action_id ?? null,
    uncertainties_count: Number(c.uncertainties_count ?? 0),
    variables_count: Number(c.variables_count ?? 0),
    has_approved_pdf: !!c.has_approved_pdf,
    updated_at: c.updated_at || null,
    approved_at: c.approved_at || null,
    sent_for_signature_at: c.sent_for_signature_at || null,
    signed_at: c.signed_at || null,
    is_pending_review: c.status === "pending_review",
    is_terminal: CONTRACT_TERMINAL_STATUSES.has(c.status),
    is_open: !CONTRACT_TERMINAL_STATUSES.has(c.status),
    requires_attention: (
      c.status === "pending_review" ||
      Number(c.uncertainties_count ?? 0) > 0 ||
      c.status === "drafting"
    ),
  }));
}

export function executionContractCounts(contracts) {
  const rows = executionContractRows(contracts);
  return {
    total: rows.length,
    open: rows.filter((r) => r.is_open).length,
    pending_review: rows.filter((r) => r.is_pending_review).length,
    drafting: rows.filter((r) => r.status === "drafting").length,
    approved: rows.filter((r) => r.status === "approved").length,
    sent_for_signature: rows.filter((r) => r.status === "sent_for_signature").length,
    signed: rows.filter((r) => r.status === "signed").length,
    with_uncertainties: rows.filter((r) => r.uncertainties_count > 0).length,
    requires_attention: rows.filter((r) => r.requires_attention).length,
  };
}


// ── Obligations — backend has NO obligation model. ─────────────────
//
// The closest authoritative signal is `preparedness_observations`
// from the Step 12 deal-situation aggregate. We surface those exactly
// (label untouched, IDs preserved) and NEVER call them obligations
// in the derived data — the component labels its section
// "Åtaganden & beredskapsgap" so the reader can see both possibilities
// at a glance.

export function executionObligationRows(situation) {
  const raw = Array.isArray(situation?.preparedness_observations)
    ? situation.preparedness_observations
    : [];
  return raw.map((obs, idx) => ({
    key: `prep.${obs.kind || idx}`,
    kind: obs.kind || null,
    label: obs.label || null,
    detail: obs.detail || null,
    fact_or_inference: obs.fact_or_inference || null,
    raised_by_item_ids: (obs.raised_by_item_ids || []).slice(),
    raised_by_acquirer_ids: (obs.raised_by_acquirer_ids || []).slice(),
    source: (obs.deal_snapshot_source || {}).provenance || null,
    user_action_required: !!obs.user_action_required,
  }));
}


// ── Risk projections ───────────────────────────────────────────────
//
// Reuse the Step 19 extractor — same shape, same fact_or_inference
// discipline. If deal snapshot missing, this simply yields [].

export function executionRiskRows(dealSnapshot) {
  return extractRiskFindings(dealSnapshot).map((r) => ({
    key: r.key,
    name: r.title,
    severity: r.severity,
    summary: r.summary,
    source: r.source || null,
    fact_or_inference: r.fact_or_inference || null,
    ask_prompt: r.ask_prompt,
  }));
}


// ── Buyer projections ──────────────────────────────────────────────
//
// Deterministic silence / open-items / bounce counts. Same fields the
// buyer workspace (Step 22) already uses.

export function executionBuyerRows(portfolio) {
  if (!Array.isArray(portfolio)) return [];
  return portfolio.map((row) => ({
    acquirer_id: row.acquirer?.id,
    acquirer_name: row.acquirer?.name || null,
    is_silent: !!row.silence?.is_silent,
    days_since_last_outbound: row.silence?.days_since_last_outbound ?? null,
    threshold_days: row.silence?.threshold_days ?? null,
    open_items_count: Number(row.open_items_count ?? 0),
    bounce_count: Number(row.communication?.bounce_count ?? 0),
    last_inbound_at: row.communication?.last_inbound_at || null,
    last_outbound_sent_at: row.communication?.last_outbound_sent_at || null,
  }));
}


// ── Attention flags (workspace-level chips) ────────────────────────
//
// One chip per deterministic signal. Never a synthesized score. The
// chip label matches the READINESS_LANGUAGE convention above.

export function executionAttentionFlags({
  actions,
  contracts,
  situation,
  now = Date.now(),
} = {}) {
  const flags = [];
  const aCounts = executionActionCounts(actions, { now });
  const cCounts = executionContractCounts(contracts);
  const risks = executionRiskRows(situation?.deal);
  const buyerRows = executionBuyerRows(situation?.buyer_portfolio);
  const preparedness = executionObligationRows(situation);

  if (aCounts.blocked > 0) {
    flags.push({
      kind: "actions_blocked",
      severity: SEVERITY.HIGH,
      label: `${aCounts.blocked} blockerade åtgärder`,
      title: READINESS_LANGUAGE.BLOCKED,
    });
  }
  if (aCounts.overdue > 0) {
    flags.push({
      kind: "actions_overdue",
      severity: SEVERITY.HIGH,
      label: `${aCounts.overdue} försenade åtgärder`,
      title: READINESS_LANGUAGE.OVERDUE,
    });
  }
  if (aCounts.pending_review > 0) {
    flags.push({
      kind: "actions_pending_review",
      severity: SEVERITY.MEDIUM,
      label: `${aCounts.pending_review} åtgärder väntar granskning`,
      title: READINESS_LANGUAGE.PENDING_REVIEW,
    });
  }
  if (cCounts.pending_review > 0) {
    flags.push({
      kind: "contracts_pending_review",
      severity: SEVERITY.MEDIUM,
      label: `${cCounts.pending_review} avtal väntar granskning`,
      title: READINESS_LANGUAGE.PENDING_REVIEW,
    });
  }
  if (cCounts.with_uncertainties > 0) {
    flags.push({
      kind: "contracts_uncertainties",
      severity: SEVERITY.MEDIUM,
      label: `${cCounts.with_uncertainties} avtal med osäkerheter`,
      title: READINESS_LANGUAGE.UNCERTAINTY,
    });
  }
  const silent = buyerRows.filter((b) => b.is_silent).length;
  if (silent > 0) {
    flags.push({
      kind: "buyers_silent",
      severity: SEVERITY.MEDIUM,
      label: `${silent} tysta köpare`,
      title: READINESS_LANGUAGE.FOLLOW_UP,
    });
  }
  const highRisks = risks.filter((r) => r.severity === SEVERITY.HIGH).length;
  if (highRisks > 0) {
    flags.push({
      kind: "risks_high",
      severity: SEVERITY.HIGH,
      label: `${highRisks} risker (hög)`,
      title: READINESS_LANGUAGE.FOLLOW_UP,
    });
  }
  if (preparedness.length > 0) {
    flags.push({
      kind: "preparedness_gaps",
      severity: SEVERITY.MEDIUM,
      label: `${preparedness.length} beredskapsgap`,
      title: READINESS_LANGUAGE.FOLLOW_UP,
    });
  }
  return flags;
}


// ── Blocking / requires-attention items ────────────────────────────
//
// Every item has an actual source. Every claim traces to a
// deterministic backend state — never "this will prevent closing".

export function executionBlockingItems({
  actions,
  contracts,
  situation,
  now = Date.now(),
} = {}) {
  const items = [];
  const aRows = executionActionRows(actions, { now });
  const cRows = executionContractRows(contracts);
  const preparedness = executionObligationRows(situation);

  for (const a of aRows) {
    if (a.status === "blocked") {
      items.push({
        key: `action.blocked.${a.id}`,
        category: "action",
        severity: SEVERITY.HIGH,
        title: a.title || `Åtgärd #${a.id}`,
        reason: READINESS_LANGUAGE.BLOCKED,
        source: {
          source_type: "deal_action",
          source_id: String(a.id),
          as_of: a.updated_at,
        },
        action_id: a.id,
        acquirer_id: a.acquirer_id,
      });
    } else if (a.is_overdue) {
      items.push({
        key: `action.overdue.${a.id}`,
        category: "action",
        severity: SEVERITY.HIGH,
        title: a.title || `Åtgärd #${a.id}`,
        reason: `${READINESS_LANGUAGE.OVERDUE} (${a.due_at})`,
        source: {
          source_type: "deal_action",
          source_id: String(a.id),
          as_of: a.due_at,
        },
        action_id: a.id,
        acquirer_id: a.acquirer_id,
      });
    }
  }

  for (const c of cRows) {
    if (c.is_pending_review) {
      items.push({
        key: `contract.review.${c.id}`,
        category: "contract",
        severity: SEVERITY.MEDIUM,
        title: c.title,
        reason: READINESS_LANGUAGE.PENDING_REVIEW,
        source: {
          source_type: "contract_draft",
          source_id: String(c.id),
          as_of: c.updated_at,
        },
        contract_id: c.id,
        acquirer_id: c.acquirer_id,
        deal_action_id: c.deal_action_id,
      });
    }
    if (c.uncertainties_count > 0) {
      items.push({
        key: `contract.uncertainty.${c.id}`,
        category: "contract",
        severity: SEVERITY.MEDIUM,
        title: `${c.title} — ${c.uncertainties_count} osäkerhet${c.uncertainties_count === 1 ? "" : "er"}`,
        reason: READINESS_LANGUAGE.UNCERTAINTY,
        source: {
          source_type: "contract_draft",
          source_id: String(c.id),
          as_of: c.updated_at,
        },
        contract_id: c.id,
        acquirer_id: c.acquirer_id,
        deal_action_id: c.deal_action_id,
      });
    }
  }

  for (const p of preparedness) {
    items.push({
      key: `preparedness.${p.kind}`,
      category: "preparedness",
      severity: SEVERITY.MEDIUM,
      title: p.label || p.kind || "Beredskapsgap",
      reason: READINESS_LANGUAGE.FOLLOW_UP,
      source: p.source || null,
      raised_by_acquirer_ids: p.raised_by_acquirer_ids,
      raised_by_item_ids: p.raised_by_item_ids,
      fact_or_inference: p.fact_or_inference,
    });
  }

  return items;
}


// ── Timeline items (only where real timestamps exist) ──────────────

export function executionTimelineItems({
  actions,
  contracts,
  situation,
  now = Date.now(),
} = {}) {
  const items = [];
  const aRows = executionActionRows(actions, { now });
  const cRows = executionContractRows(contracts);

  for (const a of aRows) {
    if (a.status === "completed" && a.updated_at) {
      items.push({
        key: `t.action.completed.${a.id}`,
        bucket: "completed",
        title: a.title || `Åtgärd #${a.id}`,
        as_of: a.updated_at,
        category: "action",
        source: {
          source_type: "deal_action",
          source_id: String(a.id),
          as_of: a.updated_at,
        },
      });
    } else if (a.is_open && a.due_at) {
      items.push({
        key: `t.action.due.${a.id}`,
        bucket: a.is_overdue ? "overdue" : "upcoming",
        title: a.title || `Åtgärd #${a.id}`,
        as_of: a.due_at,
        category: "action",
        source: {
          source_type: "deal_action",
          source_id: String(a.id),
          as_of: a.due_at,
        },
      });
    }
  }

  for (const c of cRows) {
    if (c.signed_at) {
      items.push({
        key: `t.contract.signed.${c.id}`,
        bucket: "completed",
        title: `${c.title} · Signerad`,
        as_of: c.signed_at,
        category: "contract",
        source: {
          source_type: "contract_draft",
          source_id: String(c.id),
          as_of: c.signed_at,
        },
      });
    } else if (c.sent_for_signature_at) {
      items.push({
        key: `t.contract.sent.${c.id}`,
        bucket: "open",
        title: `${c.title} · Skickad för signering`,
        as_of: c.sent_for_signature_at,
        category: "contract",
        source: {
          source_type: "contract_draft",
          source_id: String(c.id),
          as_of: c.sent_for_signature_at,
        },
      });
    } else if (c.approved_at) {
      items.push({
        key: `t.contract.approved.${c.id}`,
        bucket: "open",
        title: `${c.title} · Godkänd`,
        as_of: c.approved_at,
        category: "contract",
        source: {
          source_type: "contract_draft",
          source_id: String(c.id),
          as_of: c.approved_at,
        },
      });
    }
  }

  if (situation?.generated_at) {
    items.push({
      key: "t.situation.snapshot",
      bucket: "open",
      title: "Deal Situation-snapshot",
      as_of: situation.generated_at,
      category: "situation",
      source: {
        source_type: "deal_situation",
        source_id: String(situation.workspace_id ?? ""),
        as_of: situation.generated_at,
      },
    });
  }

  // Deterministic ordering — most recent as_of first. Any item that
  // slipped through without a valid date is dropped rather than
  // silently sorted with a fabricated fallback.
  return items
    .filter((x) => _iso(x.as_of))
    .sort((a, b) => Date.parse(b.as_of) - Date.parse(a.as_of));
}

export function executionCompletedItems(opts) {
  return executionTimelineItems(opts).filter((t) => t.bucket === "completed");
}
export function executionUpcomingItems(opts) {
  return executionTimelineItems(opts).filter((t) => t.bucket === "upcoming");
}
export function executionOverdueItems(opts) {
  return executionTimelineItems(opts).filter((t) => t.bucket === "overdue");
}


// ── Cross-domain connections ───────────────────────────────────────
//
// ONLY explicit IDs. If two rows don't share an ID we do NOT connect
// them, even if their titles / acquirer names / topics overlap.

export function executionConnections({ actions, contracts, situation } = {}) {
  const connections = [];
  const aRows = executionActionRows(actions);
  const cRows = executionContractRows(contracts);
  const buyerNamesById = new Map();
  for (const row of situation?.buyer_portfolio || []) {
    if (row.acquirer?.id != null) {
      buyerNamesById.set(row.acquirer.id, row.acquirer.name);
    }
  }
  for (const a of aRows) {
    if (a.acquirer_id != null) {
      buyerNamesById.set(a.acquirer_id, a.acquirer_name || buyerNamesById.get(a.acquirer_id) || null);
    }
  }
  for (const c of cRows) {
    if (c.acquirer_id != null) {
      buyerNamesById.set(c.acquirer_id, c.acquirer_name || buyerNamesById.get(c.acquirer_id) || null);
    }
  }

  // Contract → DealAction (via ContractDraft.deal_action_id)
  for (const c of cRows) {
    if (c.deal_action_id != null) {
      connections.push({
        key: `conn.contract.${c.id}.action.${c.deal_action_id}`,
        from: { type: "contract", id: c.id, label: c.title },
        to: { type: "action", id: c.deal_action_id, label: `Åtgärd #${c.deal_action_id}` },
        relation: "contract_scoped_to_action",
      });
    }
  }

  // Contract → Buyer (via acquirer_id)
  for (const c of cRows) {
    if (c.acquirer_id != null) {
      connections.push({
        key: `conn.contract.${c.id}.buyer.${c.acquirer_id}`,
        from: { type: "contract", id: c.id, label: c.title },
        to: {
          type: "buyer",
          id: c.acquirer_id,
          label: c.acquirer_name || buyerNamesById.get(c.acquirer_id) || `Köpare #${c.acquirer_id}`,
        },
        relation: "contract_scoped_to_buyer",
      });
    }
  }

  // Action → Buyer (via acquirer_id)
  for (const a of aRows) {
    if (a.acquirer_id != null) {
      connections.push({
        key: `conn.action.${a.id}.buyer.${a.acquirer_id}`,
        from: { type: "action", id: a.id, label: a.title || `Åtgärd #${a.id}` },
        to: {
          type: "buyer",
          id: a.acquirer_id,
          label: a.acquirer_name || buyerNamesById.get(a.acquirer_id) || `Köpare #${a.acquirer_id}`,
        },
        relation: "action_scoped_to_buyer",
      });
    }
  }

  // Preparedness → Buyers (via raised_by_acquirer_ids)
  const preparedness = executionObligationRows(situation);
  for (const p of preparedness) {
    for (const acqId of p.raised_by_acquirer_ids || []) {
      connections.push({
        key: `conn.prep.${p.kind}.buyer.${acqId}`,
        from: { type: "preparedness", id: p.kind, label: p.label || p.kind },
        to: {
          type: "buyer",
          id: acqId,
          label: buyerNamesById.get(acqId) || `Köpare #${acqId}`,
        },
        relation: "preparedness_raised_by_buyer",
      });
    }
  }

  return connections;
}


// ── Findings for AIFindingCard ─────────────────────────────────────
//
// Reuse the Step 19 KIND vocabulary — do NOT create a competing
// taxonomy. AIFindingCard already knows how to render:
//   RISK, PREPAREDNESS, BUYER_SIGNAL, CONTRACT_UNCERTAINTY,
//   CONTRACT_REVIEW, ACTION_PENDING, SUGGESTED_ACTION.
//
// executionFindingsFromSituation flattens the entire execution
// workspace into one attention queue, sorted by the shared
// `attentionRank` in Step 19's helpers.

export function executionFindingsFromSituation({
  actions,
  contracts,
  situation,
  now = Date.now(),
} = {}) {
  const out = [];

  // Risks + preparedness observations already come from the deal
  // snapshot — reuse the Step 19 extractors.
  out.push(...extractRiskFindings(situation?.deal));
  out.push(...extractPreparednessFindings(situation?.preparedness_observations));

  // Contracts requiring attention.
  const cRows = executionContractRows(contracts);
  for (const c of cRows) {
    if (c.is_pending_review) {
      out.push({
        kind: KIND.CONTRACT_REVIEW,
        severity: SEVERITY.MEDIUM,
        title: `${c.title} väntar granskning`,
        summary: READINESS_LANGUAGE.PENDING_REVIEW,
        category: "Kontrakt",
        source: {
          source_type: "contract_draft",
          source_id: String(c.id),
          as_of: c.updated_at,
        },
        evidence: [],
        contract_id: c.id,
        user_action_required: true,
        ask_prompt: null,
        key: `execution.contract.review.${c.id}`,
      });
    }
    if (c.uncertainties_count > 0) {
      out.push({
        kind: KIND.CONTRACT_UNCERTAINTY,
        severity: SEVERITY.MEDIUM,
        title: `${c.title}: ${c.uncertainties_count} osäkerhet${c.uncertainties_count === 1 ? "" : "er"}`,
        summary: READINESS_LANGUAGE.UNCERTAINTY,
        category: "Kontraktsfynd",
        source: {
          source_type: "contract_draft",
          source_id: String(c.id),
          as_of: c.updated_at,
        },
        evidence: [],
        contract_id: c.id,
        ask_prompt:
          `Förklara osäkerheterna i avtalsutkastet "${c.title}". ` +
          `Använd get_contract_draft(id=${c.id}) och peka på källor.`,
        key: `execution.contract.uncertainty.${c.id}`,
      });
    }
  }

  // Actions pending human review (uses Step 19 extractor).
  out.push(...extractPendingActionFindings(actions));

  // Blocked / overdue actions surface as EXECUTION_BLOCKER-style
  // findings but reusing the ACTION_PENDING kind so no new taxonomy.
  const aRows = executionActionRows(actions, { now });
  for (const a of aRows) {
    if (a.status === "blocked") {
      out.push({
        kind: KIND.ACTION_PENDING,
        severity: SEVERITY.HIGH,
        title: `${a.title || `Åtgärd #${a.id}`} — blockerad`,
        summary: READINESS_LANGUAGE.BLOCKED,
        category: "Åtgärd",
        source: {
          source_type: "deal_action",
          source_id: String(a.id),
          as_of: a.updated_at,
        },
        evidence: [],
        action_id: a.id,
        acquirer_id: a.acquirer_id,
        user_action_required: true,
        ask_prompt:
          `Undersök varför åtgärd #${a.id} ("${a.title}") är blockerad. ` +
          `Använd get_deal_actions(filter='blocked'). Bevara status-etiketten VERBATIM.`,
        key: `execution.action.blocked.${a.id}`,
      });
    } else if (a.is_overdue) {
      out.push({
        kind: KIND.ACTION_PENDING,
        severity: SEVERITY.HIGH,
        title: `${a.title || `Åtgärd #${a.id}`} — över förfallodatum`,
        summary: `${READINESS_LANGUAGE.OVERDUE} (${a.due_at})`,
        category: "Åtgärd",
        source: {
          source_type: "deal_action",
          source_id: String(a.id),
          as_of: a.due_at,
        },
        evidence: [],
        action_id: a.id,
        acquirer_id: a.acquirer_id,
        user_action_required: true,
        ask_prompt:
          `Undersök varför åtgärd #${a.id} ("${a.title}") är över förfallodatum. ` +
          `Använd get_deal_actions(filter='overdue').`,
        key: `execution.action.overdue.${a.id}`,
      });
    }
  }

  // Buyer silence findings — deterministic observation from the
  // situation aggregate.
  const buyerRows = executionBuyerRows(situation?.buyer_portfolio);
  for (const b of buyerRows) {
    if (!b.is_silent || b.acquirer_id == null) continue;
    out.push({
      kind: KIND.BUYER_SIGNAL,
      severity: SEVERITY.MEDIUM,
      title: `${b.acquirer_name || `Köpare #${b.acquirer_id}`}: tyst ${b.days_since_last_outbound ?? "?"} dagar`,
      summary: READINESS_LANGUAGE.FOLLOW_UP,
      category: "Köparsignal",
      source: null,
      evidence: [],
      acquirer_id: b.acquirer_id,
      user_action_required: false,
      ask_prompt:
        `Vad kan förklara att ${b.acquirer_name || `köpare #${b.acquirer_id}`} ` +
        `inte har svarat på ${b.days_since_last_outbound ?? "?"} dagar? ` +
        `Använd get_buyer_intelligence(acquirer_id=${b.acquirer_id}).`,
      key: `execution.silence.${b.acquirer_id}`,
    });
  }

  return out;
}


// ── Ask Qada prompt builders ───────────────────────────────────────

export function buildExecutionAskPrompt({
  workspaceName,
  question,
} = {}) {
  const workspace = workspaceName ? ` för affären ${workspaceName}` : "";
  const suffix = question ? `\n\n${question}` : "";
  return (
    `Analysera genomförandestatusen${workspace}. Identifiera öppna ` +
    `actions, kontraktsläge, risker och köparobservationer som ` +
    `faktiskt kan kopplas till affären via IDs. Använd ` +
    `get_deal_situation, get_deal_actions, list_contract_drafts, ` +
    `get_deal_risks och get_buyer_intelligence där det behövs. ` +
    `Separera FAKTA, DETERMINISTISK SYSTEMOBSERVATION, DIN INFERENS, ` +
    `FÖRESLAGEN ÅTGÄRD och MÄNSKLIGT BESLUT. Ange källor och IDs där ` +
    `de finns.${suffix}`
  );
}

export function buildExecutionInvestigationPrompt({
  workspaceName,
  topic,
} = {}) {
  const workspace = workspaceName ? ` i affären ${workspaceName}` : "";
  const focus = topic ? `Fokusera på: ${topic}. ` : "";
  return (
    `${focus}Utred nuvarande genomförandeläge${workspace}. Följ ` +
    `investigation-flödet från GENOMFÖRANDE- OCH CLOSING-SPECIFIKA ` +
    `UTREDNINGAR: hämta deal_situation, deal_actions (open + blocked + ` +
    `overdue), contract_drafts (pending_review + with_uncertainties), ` +
    `risker och köpar-intelligence där det behövs. Korrelera endast ` +
    `via auktoritativa IDs. Uppfinn ALDRIG att affären är stängd, klar ` +
    `att stänga eller redo att undertecknas utan att backend explicit säger det.`
  );
}
